import { Box, IconButton, Tooltip, useTheme } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DragIcon, RemoveIconTwoTone, Typography } from '@room-match/shared-ui-components';
import { FormikProps, useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Draggable, DraggableProvidedDragHandleProps, Droppable } from 'react-beautiful-dnd';
import { COLORS } from 'shared/constants/COLORS';
import IMedia from 'shared/interfaces/IMedia';
import { IResumeCredit } from 'shared/interfaces/IResume';
import { ICategory, ICredit } from 'shared/interfaces/ITalent';
import { GENERATE_DATES } from 'shared/utils/common';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import MediaBinModal from '../../../../shared/components/MediaBinModal/MediaBinModal';
import CreditItem from '../CreditItem/CreditItem';
import {
  AddMediaButton,
  CustomTextInput,
  ErrorMessageContainer,
  ItemHeader,
  MainCont,
  NewItemField,
  SaveButton,
  StyledClear,
  useStyles,
  ViewMediaButton,
} from './ResumeCategoryItem.styles';
const { DARK_CONTAINER_BORDER, LIGHT_CONTAINER_BORDER } = COLORS;
interface Props {
  item: ICategory;
  addCredit: (data: IResumeCredit) => void;
  modifyCredits: (data: ICredit[]) => void;
  hideYears: boolean;
  deleteCategory: () => void;
  ifLastItem: boolean;
  deleting: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

const ResumeCategoryItem: React.FC<Props> = React.memo(
  ({ item, addCredit, modifyCredits, hideYears, deleteCategory, ifLastItem, deleting, dragHandleProps }) => {
    const classes = useStyles();
    const theme = useTheme();
    const [showAdd, setShowAdd] = useState(false);
    const [credits, setCredits] = useState(item.credits);
    const textFieldYear: any = React.useRef(null);
    const textFieldTitle: any = React.useRef(null);
    const textFieldRole: any = React.useRef(null);
    const textFieldDirector: any = React.useRef(null);
    const addButtonRef: any = React.useRef(null);
    const [checkError, setcheckError] = useState(false);
    const [showBin, setShowBin] = useState(false);
    const [showAttachedMedia, setShowAttachedMedia] = useState(false);
    const [selectedMedia, setselectedMedia] = useState<IMedia[]>([]);
    const [editMode, setEditMode] = useState(false);

    // Keyboard accessibility state
    const [focusedCreditIndex, setFocusedCreditIndex] = useState<number>(-1);
    const [keyboardMoveMode, setKeyboardMoveMode] = useState(false);
    const [keyboardInstructions, setKeyboardInstructions] = useState<string>('');

    useEffect(() => {
      setCredits(item.credits);
    }, [item]);

    // Keyboard navigation for credits
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // Only handle keyboard navigation if we're not in an input field
        if ((event.target as HTMLElement).tagName === 'INPUT' || (event.target as HTMLElement).tagName === 'TEXTAREA') {
          return;
        }

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (!keyboardMoveMode) {
              setFocusedCreditIndex((prev) => Math.min(prev + 1, credits.length - 1));
              setKeyboardInstructions('Press Enter to pick up credit, or continue with Arrow keys');
            }
            break;

          case 'ArrowUp':
            event.preventDefault();
            if (!keyboardMoveMode) {
              setFocusedCreditIndex((prev) => Math.max(prev - 1, 0));
              setKeyboardInstructions('Press Enter to pick up credit, or continue with Arrow keys');
            }
            break;

          case 'Enter':
            event.preventDefault();
            if (focusedCreditIndex >= 0 && focusedCreditIndex < credits.length) {
              if (!keyboardMoveMode) {
                setKeyboardMoveMode(true);
                setKeyboardInstructions('Use Arrow keys to move credit, Enter to drop, Escape to cancel');
              } else {
                // Drop the credit (for now, just cancel keyboard mode)
                setKeyboardMoveMode(false);
                setKeyboardInstructions('Credit moved! Use Arrow keys to navigate');
              }
            }
            break;

          case 'Escape':
            event.preventDefault();
            setKeyboardMoveMode(false);
            setFocusedCreditIndex(-1);
            setKeyboardInstructions('');
            break;
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedCreditIndex, keyboardMoveMode, credits.length]);

    // Memoized styles to prevent recalculation
    const getListStyle = useMemo(
      () => (isDraggingOver: boolean) => ({
        padding: 8,
        flex: 1,
        transition: 'all 300ms ease',
        minHeight: isDraggingOver ? '80px' : '20px',
        backgroundColor: isDraggingOver
          ? theme.palette.type === 'dark'
            ? 'rgba(64, 128, 255, 0.1)'
            : 'rgba(25, 118, 210, 0.08)'
          : 'transparent',
        border: isDraggingOver
          ? `2px dashed ${theme.palette.type === 'dark' ? COLORS.PRIMARY_BUTTON_BORDER : COLORS.PRIMARY_BUTTON_BORDER}`
          : '2px solid transparent',
        borderRadius: 8,
        position: 'relative' as const,
      }),
      [theme.palette.type],
    );

    const getItemStyle = useMemo(
      () => (isDragging: boolean, draggableStyle: any) => {
        return {
          ...draggableStyle,
          display: 'flex',
          userSelect: 'none' as const,
          borderTop: 'none',
          backgroundColor: 'transparent',
          borderRadius: isDragging ? '8px' : '0',
          boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.15)' : 'none',
          opacity: isDragging ? 0.9 : 1,
          left: '0 !important',
          top: '0 !important',
        };
      },
      [],
    );

    const initialValues: any = {
      year: '',
      title: '',
      director: '',
      role: '',
      attached_media: [],
    };

    const handleSubmit = async (values: IResumeCredit, helpers: any) => {
      textFieldYear.current.focus();
      const id = uuidv4();
      setCredits([{ ...values, attached_media: selectedMedia, id }, ...credits]);
      addCredit({ ...values, attached_media: selectedMedia, id });
      helpers.resetForm();
      setselectedMedia([]);
      setcheckError(false);
    };

    const createResumeValidationSchema: yup.SchemaOf<any> = yup.object().shape({
      // representation_type: yup.mixed<RepresentationType>().oneOf(Object.values(RepresentationType)).required(),
      year: yup.number(),
      title: yup.string().required(),
      director: yup.string(),
      role: yup.string(),
      attached_media: yup.array(),
    });

    const form: FormikProps<IResumeCredit> = useFormik({
      initialValues,
      validationSchema: createResumeValidationSchema,
      onSubmit: (values, helpers) => {
        handleSubmit(values, helpers);
        return;
      },
    });

    // Memoized drag preview creation
    const createDragPreview = useCallback(
      (credit: ICredit) => {
        return (
          <div
            style={{
              background: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff',
              border: `2px solid ${theme.palette.type === 'dark' ? COLORS.PRIMARY_BUTTON_BORDER : COLORS.PRIMARY_BUTTON_BORDER}`,
              borderRadius: 8,
              padding: '12px 16px',
              minWidth: 200,
              maxWidth: 300,
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              fontSize: 14,
              color: theme.palette.type === 'dark' ? '#fff' : '#333',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{credit.title}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {credit.role} • {hideYears ? 'Year Hidden' : credit.year}
            </div>
            <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>Moving to: {item.category}</div>
          </div>
        );
      },
      [theme.palette.type, hideYears, item.category],
    );

    // Memoized modify credit function
    const modifyCredit = useCallback(
      (creditID: string, data: ICredit | {}) => {
        if (Object.keys(data).length > 0) {
          const newCredits: any = credits?.map((m) => {
            if (m.id === creditID) {
              return { ...m, ...data };
            }
            return m;
          });

          setCredits(newCredits);
          modifyCredits(newCredits);
        } else {
          const newCredits = credits?.filter((f) => f.id !== creditID);
          setCredits(newCredits);
          modifyCredits(newCredits);
        }
      },
      [credits, modifyCredits],
    );
    const isTraining = item.category === 'Training';
    const isTheatre = item.category === 'Theatre';
    const btnDisabled = !form.values.year && !form.values.title && !form.values.role && !form.values.director;

    return (
      <MainCont>
        <ItemHeader>
          <div
            {...dragHandleProps}
            style={{
              padding: '8px 4px',
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 44,
              minHeight: 44,
              borderRadius: 4,
            }}
          >
            <DragIcon style={{ height: 18, width: 18 }} />
          </div>
          <p style={{ flex: 1, marginLeft: 13, fontSize: 16 }}>{item.category}</p>
          <IconButton
            disabled={deleting || credits.length > 0 || ifLastItem ? true : false}
            onClick={deleteCategory}
            style={{
              filter: deleting || credits.length || ifLastItem ? 'grayscale(100%)' : 'none',
              minWidth: 44,
              minHeight: 44,
            }}
          >
            <Tooltip title="Edit Category">
              <RemoveIconTwoTone style={{ height: 16, width: 'auto' }} />
            </Tooltip>
          </IconButton>
        </ItemHeader>
        <NewItemField>
          <Autocomplete
            id="resume-year"
            onChange={(e, value: any) => {
              if (value && value?.key) {
                form.setFieldValue('year', value.key);
              }
            }}
            value={{ key: form.values.year.toString(), value: form.values.year }}
            options={GENERATE_DATES}
            ListboxProps={{ style: { maxHeight: 250 } }}
            noOptionsText="Invalid Year"
            getOptionLabel={(option: any) => option.key}
            classes={{ option: classes.option }}
            renderOption={(option: any) => {
              return <p style={{ fontSize: 16 }}>{option.value}</p>;
            }}
            renderInput={(params: any) => (
              <CustomTextInput
                error={checkError && form.errors.year !== undefined}
                inputRef={textFieldYear}
                placeholder="Year"
                {...params}
                inputProps={{
                  ...params.inputProps,
                  onKeyPress: (event) => {
                    const { key } = event;
                    if (key === 'Enter') {
                      form.setFieldValue('year', params.inputProps.value);
                      textFieldTitle.current.focus();
                    }
                  },
                }}
              />
            )}
          />

          <CustomTextInput
            error={checkError && form.errors.title !== undefined}
            inputRef={textFieldTitle}
            size="small"
            fullWidth
            placeholder={isTraining ? 'Type' : 'Title'}
            InputLabelProps={{ shrink: true }}
            name="title"
            value={form.values.title}
            onChange={form.handleChange}
            inputProps={{
              onKeyPress: (event) => {
                const { key } = event;
                if (key === 'Enter') {
                  textFieldRole.current.focus();
                }
              },
            }}
          />
          <CustomTextInput
            error={checkError && form.errors.role !== undefined}
            inputRef={textFieldRole}
            size="small"
            fullWidth
            placeholder={isTraining ? 'Company' : 'Role'}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            name="role"
            value={form.values.role}
            onChange={form.handleChange}
            inputProps={{
              onKeyPress: (event) => {
                const { key } = event;
                if (key === 'Enter') {
                  textFieldDirector.current.focus();
                }
              },
            }}
          />
          <CustomTextInput
            error={checkError && form.errors.director !== undefined}
            inputRef={textFieldDirector}
            size="small"
            fullWidth
            placeholder={`${item.category.includes('Print') ? 'Photographer' : isTraining ? 'Location' : isTheatre ? 'Director/Company' : 'Director/Production Co/Network'}`}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            name="director"
            value={form.values.director}
            onChange={form.handleChange}
            inputProps={{
              onKeyPress: (event) => {
                const { key } = event;
                if (key === 'Enter') {
                  addButtonRef.current.click();
                }
              },
            }}
          />
          <AddMediaButton
            onClick={() => setShowBin(true)}
            disabled={btnDisabled}
            style={{ opacity: btnDisabled ? 0.5 : 1 }}
          >
            Add Media
          </AddMediaButton>
          <Tooltip title="Save">
            <SaveButton
              ref={addButtonRef}
              variant="contained"
              onClick={() => {
                setcheckError(true);
                form.handleSubmit();
              }}
            >
              Save
            </SaveButton>
          </Tooltip>
          <Tooltip title="Clear Fields">
            <IconButton
              style={{ padding: 0 }}
              onClick={() => {
                form.resetForm();
                setselectedMedia([]);
              }}
            >
              <StyledClear />
            </IconButton>
          </Tooltip>
          {checkError && Object.keys(form.errors).length > 0 && (
            <ErrorMessageContainer>
              <Typography color="errorText" fontSize={14}>
                {isTraining ? '*Type is required.' : '*Title is required.'}
              </Typography>
            </ErrorMessageContainer>
          )}
          {selectedMedia?.length ? (
            <Box style={{ display: 'flex', alignItems: 'center', marginTop: 10, marginLeft: 10 }}>
              <p style={{ fontSize: 16 }}> {selectedMedia?.length} Media Attached : </p>
              <ViewMediaButton disabled={selectedMedia?.length === 0} onClick={() => setShowAttachedMedia(true)}>
                View Media
              </ViewMediaButton>
            </Box>
          ) : (
            <></>
          )}
        </NewItemField>
        <Droppable droppableId={item.category_id} type="CREDIT">
          {(provided, snapshot) => (
            <div
              className={`credit-item-list disabled-${editMode}`}
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {snapshot.isDraggingOver && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: theme.palette.type === 'dark' ? COLORS.PRIMARY_BUTTON_BORDER : COLORS.PRIMARY_BUTTON_BORDER,
                    fontSize: 12,
                    fontWeight: 500,
                    opacity: 0.5,
                    pointerEvents: 'none',
                    zIndex: 1,
                    backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '4px 8px',
                    borderRadius: 4,
                    backdropFilter: 'blur(2px)',
                  }}
                >
                  Drop here
                </div>
              )}
              {credits?.length ? (
                credits?.map((value: ICredit, index: number) => (
                  <Draggable key={value.id} draggableId={value.id} index={index} isDragDisabled={editMode}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...getItemStyle(snapshot.isDragging, provided.draggableProps.style),
                          outline: focusedCreditIndex === index ? `3px solid ${COLORS.PRIMARY_BUTTON_BORDER}` : 'none',
                          backgroundColor:
                            focusedCreditIndex === index
                              ? theme.palette.type === 'dark'
                                ? 'rgba(64, 128, 255, 0.1)'
                                : 'rgba(25, 118, 210, 0.08)'
                              : snapshot.isDragging
                                ? theme.palette.type === 'dark'
                                  ? 'rgba(42, 42, 42, 0.95)'
                                  : 'rgba(255, 255, 255, 0.95)'
                                : 'transparent',
                          position: 'relative',
                        }}
                      >
                        {keyboardMoveMode && focusedCreditIndex === index && (
                          <div
                            style={{
                              position: 'absolute',
                              top: -8,
                              left: -8,
                              background: COLORS.PRIMARY_BUTTON_BORDER,
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 600,
                              zIndex: 1000,
                            }}
                          >
                            MOVING
                          </div>
                        )}
                        <CreditItem
                          hideYears={hideYears}
                          isPhotographer={item.category.includes('Print')}
                          edit={setEditMode}
                          modifyCredit={(data: ICredit | {}) => modifyCredit(value.id, data)}
                          item={value}
                          key={value.id}
                          dragHandleProps={provided.dragHandleProps}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              ) : snapshot.isDraggingOver ? (
                <div
                  style={{
                    padding: '20px',
                    textAlign: 'center' as const,
                    color: theme.palette.type === 'dark' ? COLORS.PRIMARY_TEXT : COLORS.LIGHT_TEXT_FADED_BORDER,
                    fontSize: 14,
                    fontStyle: 'italic',
                  }}
                >
                  No credits yet - drop one here
                </div>
              ) : (
                <></>
              )}
              {provided.placeholder}
              {keyboardInstructions && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: -40,
                    left: 0,
                    right: 0,
                    background: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
                    color: theme.palette.type === 'dark' ? COLORS.PRIMARY_TEXT : '#333',
                    padding: '8px 12px',
                    borderRadius: 4,
                    fontSize: 12,
                    textAlign: 'center' as const,
                    border: `1px solid ${COLORS.PRIMARY_BUTTON_BORDER}`,
                    zIndex: 1000,
                  }}
                >
                  {keyboardInstructions}
                </div>
              )}
            </div>
          )}
        </Droppable>
        {showBin && (
          <MediaBinModal
            selected={selectedMedia}
            show={showBin}
            close={() => setShowBin(false)}
            setSelected={setselectedMedia}
          />
        )}
        {showAttachedMedia && (
          <MediaBinModal
            attachedMedia
            selected={selectedMedia}
            show={showAttachedMedia}
            close={() => setShowAttachedMedia(false)}
            setSelected={setselectedMedia}
          />
        )}
      </MainCont>
    );
  },
);

export default ResumeCategoryItem;
